"use client";

import React, { useCallback, useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import { usePostHog } from "@posthog/next";
import { IClosedInlineWidget } from "@/components/common/IClosedInlineWidget";
import { buildIClosedSchedulerUrl, ICLOSED_EVENT_TITLE } from "@/utils/iclosed";
import { DEMO_SOURCE_DIRECT } from "@/utils/demoSource";

interface MarketingContactSalesSchedulerProps {
  /** Work email captured upstream (`?email=`), prefilled into the scheduler. */
  prefillEmail?: string;
  /** `?source=` attribution resolved server-side from the URL. */
  source?: string;
}

/**
 * Contact-sales booking experience: the iClosed scheduler embedded directly.
 * The previous lead-capture form was removed — iClosed collects contact details
 * and tracks partial bookings natively, so a pre-form only added an awkward
 * two-step flow.
 *
 * The cached HTML uses the default scheduler. `?email=` and `?source=` are
 * applied after mount so the page can stay on the CDN.
 */
function subscribeToLocation() {
  return () => {};
}

type SchedulerQuery = { email?: string; source?: string };

let cachedSchedulerKey = "";
let cachedSchedulerQuery: SchedulerQuery = {};
let cachedServerSchedulerKey = "";
let cachedServerSchedulerQuery: SchedulerQuery = {};

function getServerSchedulerSnapshot(
  prefillEmail?: string,
  source?: string,
): SchedulerQuery {
  const key = `${prefillEmail ?? ""}\0${source ?? ""}`;
  if (key !== cachedServerSchedulerKey) {
    cachedServerSchedulerKey = key;
    cachedServerSchedulerQuery = { email: prefillEmail, source };
  }
  return cachedServerSchedulerQuery;
}

function getSchedulerSnapshot(
  prefillEmail?: string,
  source?: string,
): SchedulerQuery {
  const key = `${window.location.search}\0${prefillEmail ?? ""}\0${source ?? ""}`;
  if (key !== cachedSchedulerKey) {
    cachedSchedulerKey = key;
    const params = new URLSearchParams(window.location.search);
    cachedSchedulerQuery = {
      email: params.get("email")?.trim() || prefillEmail,
      source: params.get("source")?.trim() || source,
    };
  }
  return cachedSchedulerQuery;
}

const MarketingContactSalesScheduler: React.FC<
  MarketingContactSalesSchedulerProps
> = ({ prefillEmail, source }) => {
  const { t, i18n } = useTranslation();
  const posthog = usePostHog();
  const query = useSyncExternalStore(
    subscribeToLocation,
    () => getSchedulerSnapshot(prefillEmail, source),
    () => getServerSchedulerSnapshot(prefillEmail, source),
  );

  const resolvedSource = query.source ?? DEMO_SOURCE_DIRECT;
  const schedulerUrl = buildIClosedSchedulerUrl(
    query.email ? { email: query.email } : {},
  );

  // Record the demo-page arrival so we can measure conversion rate by source
  // (direct vs nav vs pricing vs hero, etc.).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nextSource = params.get("source")?.trim() || source || DEMO_SOURCE_DIRECT;
    try {
      posthog.capture("marketing_demo_page_viewed", {
        source: nextSource,
        language: i18n.language,
      });
    } catch {
      // analytics being unavailable must not break the page
    }
    // Capture once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fired by the embedded iClosed scheduler once the prospect books a time
  // (via its `iclosed.call_scheduled` postMessage).
  const handleMeetingBooked = useCallback(() => {
    try {
      posthog.capture("marketing_demo_meeting_booked", {
        source: resolvedSource,
        language: i18n.language,
      });
    } catch {
      // ignore analytics failures
    }
  }, [i18n.language, posthog, resolvedSource]);

  return (
    <div className="rounded-3xl border border-black/10 bg-marketing-voice-card p-6 md:p-10">
      <IClosedInlineWidget
        url={schedulerUrl}
        title={ICLOSED_EVENT_TITLE}
        onBooked={handleMeetingBooked}
        fallbackLabel={t("redesign.contactSales.form.schedulingFallback")}
      />
    </div>
  );
};

export default MarketingContactSalesScheduler;
